import { useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FuenteDeLaPropuesta as FuenteDeLaPropuestaDeLaIa } from "@/lib/rellenoConIa";
import type { ClaseDeSuelo } from "@/lib/suelos";
import { cn } from "@/lib/utils";

import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import {
  compararConLosRoles,
  contarValidadas,
  textoDeLaComparacion,
  textoDeLasValidadas,
} from "./cuadroDeSuelos";
import {
  etiquetaDeUnidad,
  formatearSuperficie,
  parsearSuperficie,
  sumarSuperficies,
} from "./superficies";

/**
 * Una fila del cuadro, mientras se edita.
 *
 * Lleva `clave` propia y no se apoya en el índice del arreglo: quitar la
 * segunda de cuatro renumeraría a las de abajo, y React reusaría el input de
 * una fila para otra — lo escrito saltaría de renglón. Tampoco puede ser el
 * `id` del servidor: una fila recién agregada todavía no tiene.
 */
export interface FilaDeSuelo {
  clave: string;
  /**
   * El `id` del servidor cuando la fila ya está guardada; ausente en una
   * recién agregada. Viaja en el guardado: el servidor reconcilia por `id`
   * porque de la fila cuelga su línea de valorización.
   */
  id?: number;
  sueloClaseId: number | null;
  descripcion: string;
  superficie: string;
  validada: boolean;
}

/**
 * Una fila vacía, con la clave que la identifica mientras se edita.
 *
 * NO se exporta: solo la usa el botón "Agregar clase de uso" de este mismo
 * componente, y exportar una función desde un archivo de componente rompe el
 * refresco en caliente de Vite.
 */
function filaDeSueloVacia(): FilaDeSuelo {
  return {
    clave: crypto.randomUUID(),
    sueloClaseId: null,
    descripcion: "",
    superficie: "",
    validada: false,
  };
}

/** Las cinco columnas del diseño: clase, descripción, superficie, validada y quitar. */
const COLUMNAS = "grid grid-cols-[9.375rem_minmax(0,1fr)_7.5rem_5.75rem_4rem] items-center gap-2.5";

interface TablaDeClasesDeSueloProps {
  filas: FilaDeSuelo[];
  /** El catálogo con el que se llena el selector de cada fila. */
  clases: ClaseDeSuelo[];
  /** La unidad que declara el INFORME, no una constante. */
  unidad: "ha" | "m2";
  /**
   * La superficie de cada rol del informe, en la MISMA unidad. Es contra lo que
   * se compara el subtotal: advierte la diferencia y nunca bloquea (RF-E1 §8.2).
   */
  superficiesDeLosRoles?: (string | null)[];
  disabled?: boolean;
  /**
   * De qué documento dice la IA que salió cada fila propuesta, por clave. Solo
   * mientras la propuesta se revisa; una fila sin clave aquí no es de la IA.
   */
  fuentes?: Record<string, FuenteDeLaPropuestaDeLaIa | null>;
  onCambiar: (filas: FilaDeSuelo[]) => void;
}

/**
 * La tarjeta 03 "Clases de uso de suelo y superficie": las filas se editan EN
 * LÍNEA y el pie dice el subtotal y cómo queda frente a los roles.
 *
 * LA CLASE SALE DE UN CATÁLOGO Y NO DE UNA LISTA ESCRITA ACÁ. El prototipo
 * dibuja un selector de ocho opciones (Clase I a VIII) y
 * `prototipado/modelo-de-datos.md` §9.2 lo contradice con evidencia medida
 * contra informes reales: el código es COMPUESTO y admite rango —`IIIr-IVr`,
 * `IV-V`— donde el sufijo `r` distingue suelo regado. Ocho opciones fijas
 * rechazarían un código que Consultek ya emitió; el catálogo se administra en
 * `/admin/clases-de-suelo` y crece sin desplegar.
 *
 * LA SUPERFICIE VA EN LA UNIDAD DEL INFORME y no en "há" fijas, aunque el
 * prototipo rotule la columna así: este cuadro es un desglose del mismo predio,
 * y con otra unidad su subtotal y la superficie de los roles dejarían de poder
 * compararse.
 *
 * LA MISMA CLASE PUEDE REPETIRSE, y no es un descuido: el cuadro medido trae
 * dos filas "Clase III" —"planos parte plantados" y "planos
 * arrancados/despejados"—, que son dos superficies distintas del mismo tipo de
 * suelo. Por eso el selector no descarta lo ya elegido.
 *
 * LA CASILLA ES POR FILA y no una al pie del cuadro. Con nueve clases, una
 * sola no diría cuál de las nueve se revisó — y eso es exactamente lo que
 * existe para garantizar, porque el cuadro se puede rellenar con IA. Por lo
 * mismo, EDITAR una fila la desmarca: lo validado era lo que decía antes.
 *
 * QUITAR PIDE CONFIRMACIÓN EN LA MISMA FILA ("¿Quitar? Sí / No"), igual que los
 * archivos de antecedentes: una fila con superficie cargada se pierde sin
 * deshacer hasta que se guarde la pestaña.
 *
 * EL SUBTOTAL SUMA EN CENTÉSIMAS (`sumarSuperficies`), igual que el de los
 * roles: se muestra al lado de los números que la persona escribió, y en punto
 * flotante `0,1 + 0,2` no da `0,3` — aparecería una diferencia que no existe.
 */
export function TablaDeClasesDeSuelo({
  filas,
  clases,
  unidad,
  superficiesDeLosRoles = [],
  disabled = false,
  fuentes,
  onCambiar,
}: TablaDeClasesDeSueloProps) {
  const unidadLegible = etiquetaDeUnidad(unidad);
  const subtotal = sumarSuperficies(filas.map((fila) => fila.superficie));
  const comparacion = compararConLosRoles(subtotal, superficiesDeLosRoles);
  const validadas = contarValidadas(filas);
  const todasValidadas = validadas.total > 0 && validadas.validadas === validadas.total;

  /** La fila que está preguntando "¿Quitar?", por su clave. */
  const [confirmando, setConfirmando] = useState<string | null>(null);

  /** Editar un dato desmarca la validación de ESA fila: lo revisado era otro valor. */
  function editar(clave: string, cambio: Partial<Omit<FilaDeSuelo, "clave" | "validada">>) {
    onCambiar(
      filas.map((fila) => (fila.clave === clave ? { ...fila, ...cambio, validada: false } : fila)),
    );
  }

  function validar(clave: string, validada: boolean) {
    onCambiar(filas.map((fila) => (fila.clave === clave ? { ...fila, validada } : fila)));
  }

  return (
    <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
      <EncabezadoNumerado
        numero="03"
        idDelTitulo="suelos-clases-titulo"
        titulo="Clases de uso de suelo y superficie"
        bajada="Una fila por clase. La suma debería coincidir con la superficie del predio."
        acciones={
          <span
            className={cn(
              "rounded-[0.4375rem] px-2.5 py-[0.3125rem] font-mono text-[0.6875rem] font-bold",
              todasValidadas
                ? "bg-success-soft text-success-soft-foreground"
                : "bg-muted text-ink-body",
            )}
          >
            {textoDeLasValidadas(validadas)}
          </span>
        }
      />

      <div className="border-border mt-3.5 overflow-x-auto rounded-lg border-[1.5px]">
        <div className="min-w-[44rem]">
          <div className={cn(COLUMNAS, "bg-superficie-sutil border-divisor border-b px-3 py-2")}>
            <span className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.04em] uppercase">
              Clase
            </span>
            <span className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.04em] uppercase">
              Descripción
            </span>
            <span className="text-ink-muted text-right text-[0.65625rem] font-extrabold tracking-[0.04em] uppercase">
              Superficie {unidadLegible}
            </span>
            <span className="text-ink-muted text-center text-[0.65625rem] font-extrabold tracking-[0.04em] uppercase">
              Validada
            </span>
            <span />
          </div>

          {filas.length === 0 ? (
            <p className="text-ink-muted border-divisor border-b px-3 py-4 text-sm">
              Todavía no hay clases de uso cargadas. Agrega la primera con el botón de abajo.
            </p>
          ) : (
            filas.map((fila, indice) => {
              const numero = indice + 1;
              const quitando = confirmando === fila.clave;
              // Un texto que no es número se marca: no suma, y sin aviso la
              // fila parecería cargada.
              const superficieIlegible =
                fila.superficie.trim() !== "" && parsearSuperficie(fila.superficie) === null;

              return (
                <div
                  key={fila.clave}
                  className={cn(
                    COLUMNAS,
                    "border-divisor border-b px-3 py-2",
                    quitando && "bg-danger-soft/50",
                  )}
                >
                  {/* `aria-label` y no un `<Label>` suelto al lado: un combobox
                      de Radix no se nombra con su contenido, así que sin esto
                      el control queda mudo para un lector de pantalla y para
                      los tests. CONTROLADO SIEMPRE: `""` y no `undefined` para
                      la fila sin clase; con `undefined` el Select nace NO
                      CONTROLADO y pasa a controlado al elegir. */}
                  <Select
                    value={fila.sueloClaseId === null ? "" : String(fila.sueloClaseId)}
                    disabled={disabled}
                    onValueChange={(valor) => editar(fila.clave, { sueloClaseId: Number(valor) })}
                  >
                    <SelectTrigger
                      size="sm"
                      className="w-full rounded-[0.625rem] px-2 font-mono text-[0.8125rem] font-bold"
                      aria-label={`Clase de uso, fila ${numero}`}
                    >
                      <SelectValue placeholder="Clase" />
                    </SelectTrigger>
                    <SelectContent>
                      {clases.map((clase) => (
                        <SelectItem key={clase.id} value={String(clase.id)}>
                          {clase.codigo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    className="h-9 min-w-0 rounded-[0.625rem] px-[0.6875rem] text-[0.8125rem]"
                    aria-label={`Descripción de la fila ${numero}`}
                    placeholder="Ej.: planicies y lomajes con praderas"
                    value={fila.descripcion}
                    disabled={disabled}
                    onChange={(evento) => editar(fila.clave, { descripcion: evento.target.value })}
                  />

                  {/* `inputMode="decimal"` y no `type="number"`: en Chile se
                      escribe con COMA y un input numérico la rechaza según el
                      idioma del navegador. `parsearSuperficie` entiende las dos. */}
                  <Input
                    className={cn(
                      "h-9 min-w-0 rounded-[0.625rem] px-[0.6875rem] text-right font-mono text-[0.8125rem] font-bold tabular-nums",
                      superficieIlegible && "border-warning-dot",
                    )}
                    inputMode="decimal"
                    aria-label={`Superficie de la fila ${numero}, en ${unidadLegible}`}
                    placeholder="0,00"
                    value={fila.superficie}
                    disabled={disabled}
                    onChange={(evento) => editar(fila.clave, { superficie: evento.target.value })}
                  />

                  <span className="flex justify-center">
                    <Checkbox
                      checked={fila.validada}
                      disabled={disabled}
                      title={fila.validada ? "Quitar validación" : "Marcar como validada"}
                      aria-label={`Validar la fila ${numero}`}
                      onCheckedChange={(valor) => validar(fila.clave, valor === true)}
                    />
                  </span>

                  <span className="flex justify-end">
                    {quitando ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="sr-only">¿Quitar?</span>
                        <button
                          type="button"
                          aria-label={`Sí, quitar la fila ${numero}`}
                          onClick={() => {
                            setConfirmando(null);
                            onCambiar(filas.filter((otra) => otra.clave !== fila.clave));
                          }}
                          className="bg-destructive rounded-[0.4375rem] px-2 py-1 text-[0.71875rem] font-bold text-white"
                        >
                          Sí
                        </button>
                        <button
                          type="button"
                          aria-label={`No quitar la fila ${numero}`}
                          onClick={() => setConfirmando(null)}
                          className="text-ink-body border-input rounded-[0.4375rem] border-[1.5px] px-[0.4375rem] py-[0.1875rem] text-[0.71875rem] font-bold"
                        >
                          No
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={disabled}
                        aria-label={`Quitar la fila ${numero}`}
                        onClick={() => setConfirmando(fila.clave)}
                        className="text-danger-soft-foreground hover:bg-danger-soft rounded-sm px-2 py-[0.3125rem] text-xs font-bold disabled:opacity-50"
                      >
                        Quitar
                      </button>
                    )}
                  </span>

                  {fuentes && fila.clave in fuentes ? (
                    <div className="col-span-full">
                      <FuenteDeLaPropuesta fuente={fuentes[fila.clave]} />
                    </div>
                  ) : null}
                </div>
              );
            })
          )}

          <div className="bg-superficie-sutil flex flex-wrap items-center gap-x-3.5 gap-y-2 px-3 py-2.5">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onCambiar([...filas, filaDeSueloVacia()])}
              className="text-success-soft-foreground hover:text-ink inline-flex items-center gap-2 text-[0.78125rem] font-bold disabled:opacity-50"
            >
              <span
                aria-hidden="true"
                className="border-success-dot/60 flex size-5.5 items-center justify-center rounded-[0.4375rem] border-[1.5px] border-dashed text-sm leading-none"
              >
                +
              </span>
              Agregar clase de uso
            </button>

            {/* Un chip y no un bloqueo: RF-E1 §8.2 manda advertir. */}
            <span
              className={cn(
                "ml-auto inline-flex items-center rounded-[0.4375rem] px-[0.5625rem] py-1 text-[0.71875rem] font-bold",
                comparacion.tipo === "coincide" && "bg-success-soft text-success-soft-foreground",
                comparacion.tipo === "difiere" && "bg-warning-soft text-warning-soft-foreground",
                comparacion.tipo === "sin_roles" && "bg-muted text-ink-muted",
              )}
            >
              {textoDeLaComparacion(comparacion, unidadLegible)}
            </span>

            {/* El subtotal describe lo ESCRITO, así que se dibuja aunque alguna
                fila no tenga superficie: esas no suman, y no decirlo dejaría
                al cuadro sin su cifra al pie por una fila a medio llenar. */}
            <span className="text-ink-body text-[0.78125rem] font-bold">
              Subtotal{" "}
              <span className="text-ink font-mono text-sm tabular-nums">
                {formatearSuperficie(subtotal)} {unidadLegible}
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
